import BaseMenu from '@c/menus/BaseMenu';
import { Popover } from '@headlessui/react';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import { useQuery } from '@tanstack/react-query';
import Button, { ButtonStyles } from '@ui/Button';
import { getAllCuratedLists } from '@util/firestore/curated/curated.service';
import {
  addProductToCuratedList,
  removeProductFromCuratedList,
} from '@util/firestore/products';
import { useWarehouseVariations } from '@util/hooks/useWarehouseVariations';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { ProductDocument } from 'models/product';
import { useRouter } from '@bprogress/next';

const AddToCuratedList = ({ product }: { product: ProductDocument }) => {
  const { userDoc } = useAuth();
  const { showSuccessToast } = useToastContext();
  const router = useRouter();

  const { data: all_curated_lists } = useQuery({
    queryKey: ['curated-lists'],
    queryFn: () => getAllCuratedLists(),
    enabled:
      !!userDoc?.roles?.admin ||
      !!userDoc?.roles?.curated_lists ||
      !!userDoc?.roles?.support,
  });
  all_curated_lists?.sort((a, b) =>
    a.index && !b.index
      ? -1
      : b.index && !a.index
      ? 1
      : a.index && b.index
      ? a.index - b.index
      : -1
  );

  const addToList = async (list: string) => {
    let toastText = '';
    if (product.curated_lists?.includes(list)) {
      await removeProductFromCuratedList(product.id, list);
      toastText = 'Product successfully removed from the list';
    } else {
      await addProductToCuratedList(product.id, list);
      toastText = 'Product successfully added to the list';
    }
    router.refresh();
    showSuccessToast(toastText);
  };

  const { relatedItems } = useWarehouseVariations(product);

  const addGroupToList = async (list: string) => {
    for (const item of relatedItems) {
      if (product.curated_lists?.includes(list)) {
        await removeProductFromCuratedList(item.id, list);
      } else {
        await addProductToCuratedList(item.id, list);
      }
    }
    showSuccessToast('Products successfully added to the list');
    router.refresh();
  };

  return (
    <>
      <BaseMenu
        triggerElement={
          // silences warning: <button> cannot appear as a descendant of <button>
          <div
            className={ButtonStyles({
              type: 'tertiary',
              height: 'small',
              width: 'small',
            })}
          >
            Add To List
          </div>
        }
      >
        <div className="z-50 flex flex-col items-start gap-[1.6rem] rounded-2xl bg-brand-white p-[1.6rem] shadow-md">
          <Popover>
            {all_curated_lists?.map((list) => (
              <Button
                key={list.slug}
                leadingIcon={
                  product.curated_lists?.some((l) => l === list.slug) && (
                    <CheckCircleIcon width={24} height={24} />
                  )
                }
                text={list.slug + (list.index ? ` (${list.index})` : '')}
                type="text"
                width="small"
                onClick={() => addToList(list.slug)}
              />
            ))}
          </Popover>
        </div>
      </BaseMenu>
      {product.group_id && (
        <BaseMenu
          triggerElement={
            // silences warning: <button> cannot appear as a descendant of <button>
            <div
              className={ButtonStyles({
                type: 'tertiary',
                height: 'small',
                width: 'small',
              })}
            >
              Add Group List
            </div>
          }
        >
          <div className="z-50 flex flex-col items-start gap-[1.6rem] rounded-2xl bg-brand-white p-[1.6rem] shadow-md">
            <Popover>
              {all_curated_lists?.map((list) => (
                <Button
                  key={list.slug}
                  leadingIcon={
                    product.curated_lists?.some((l) => l === list.slug) && (
                      <CheckCircleIcon width={24} height={24} />
                    )
                  }
                  text={list.slug + (list.index ? ` (${list.index})` : '')}
                  type="text"
                  width="small"
                  onClick={() => addGroupToList(list.slug)}
                />
              ))}
            </Popover>
          </div>
        </BaseMenu>
      )}
    </>
  );
};

export default AddToCuratedList;
